// crewbit-admin/src/hooks/useInvoiceBranding.ts
//
// Brand-only consumer of `invoice_settings`. Reads the 7 brand-only columns
// directly (per Rule #15 — named SELECT, no .from('invoice_settings').upsert
// writer); writes route through the canonical update_invoice_settings RPC
// (Migration 296 / Decision #584; consolidates all client-side writes per
// §InvoiceSettings.LegacyConsumerRetirement final close).
//
// Logo upload uses stable filename `${businessId}/logo.${ext}` with
// upsert: true so re-uploads overwrite in place — no orphan accumulation.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { log } from '../lib/logger';
import { withTimeout } from '../lib/withTimeout';
import { updateInvoiceSettings, type AcceptedMethod } from '../lib/invoiceSettingsMutations';
import { palette } from '../styles/tokens';

export interface InvoiceBranding {
  id: string;
  business_id: string;
  logo_url: string | null;
  accent_color: string;
  invoice_prefix: string;
  default_note: string;
  default_thank_you: string;
  etransfer_email: string | null;
  // §Settings Phase 2B: accepted_methods replaces the retired accept_etransfer
  // column. Form derives the e-Transfer toggle from membership in this array.
  accepted_methods: AcceptedMethod[];
}

export type InvoiceBrandingPatch = Partial<Omit<InvoiceBranding, 'id' | 'business_id'>>;

const BRAND_COLUMNS =
  'id, business_id, logo_url, accent_color, invoice_prefix, default_note, default_thank_you, etransfer_email, accepted_methods';

const VALID_METHODS: readonly AcceptedMethod[] = ['card', 'etransfer', 'cash'];

function normalizeMethods(raw: unknown): AcceptedMethod[] {
  if (!Array.isArray(raw)) return ['card', 'etransfer', 'cash'];
  return (raw as unknown[])
    .filter((m): m is AcceptedMethod => typeof m === 'string' && (VALID_METHODS as readonly string[]).includes(m));
}

function defaults(businessId: string): InvoiceBranding {
  return {
    id: '',
    business_id: businessId,
    logo_url: null,
    accent_color: palette.accent,
    invoice_prefix: 'INV',
    default_note: '',
    default_thank_you: 'Thank you for your business!',
    etransfer_email: null,
    accepted_methods: ['card', 'etransfer', 'cash'],
  };
}

async function logActivity(businessId: string, desc: string, userId: string) {
  try {
    await supabase.from('activity_log').insert({
      business_id: businessId,
      activity_type: 'updated',
      description: desc,
      entity_type: 'invoice_settings',
      entity_id: businessId,
      logged_by: userId,
    });
  } catch {
    /* non-critical */
  }
}

export function useInvoiceBranding(businessId: string | undefined) {
  const [settings, setSettings] = useState<InvoiceBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('invoice_settings')
        .select(BRAND_COLUMNS)
        .eq('business_id', businessId)
        .maybeSingle();
      if (err) throw err;
      if (mountedRef.current) {
        if (data) {
          const row = data as Omit<InvoiceBranding, 'accepted_methods'> & { accepted_methods?: unknown };
          setSettings({
            ...row,
            accepted_methods: normalizeMethods(row.accepted_methods),
          });
        } else {
          setSettings(defaults(businessId));
        }
      }
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : 'Failed to load branding');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh]);

  const save = useCallback(
    async (patch: InvoiceBrandingPatch, userId: string): Promise<boolean> => {
      if (!businessId) return false;
      try {
        const result = await updateInvoiceSettings({
          businessId,
          logoUrl: patch.logo_url,
          accentColor: patch.accent_color,
          invoicePrefix: patch.invoice_prefix,
          defaultNote: patch.default_note,
          defaultThankYou: patch.default_thank_you,
          // §Settings Phase 2B: e-Transfer membership flows through acceptedMethods.
          acceptedMethods: patch.accepted_methods,
          etransferEmail: patch.etransfer_email,
        });
        if (!result.success) {
          log.error('[useInvoiceBranding] save failed', undefined, { code: result.error.code });
          return false;
        }
        if (mountedRef.current) {
          setSettings((prev) => (prev ? { ...prev, ...patch } : prev));
        }
        // RPC body writes the activity_log entry; keep the legacy logActivity call
        // to preserve admin operator-attribution row (entity_id = businessId, role-aware).
        await logActivity(businessId, 'Invoice branding updated', userId);
        return true;
      } catch (e) {
        log.error('[useInvoiceBranding] save threw', e);
        return false;
      }
    },
    [businessId],
  );

  const uploadLogo = useCallback(
    async (file: File, userId: string): Promise<string | null> => {
      if (!businessId) return null;
      try {
        const ext = file.name.split('.').pop() || 'png';
        const path = `${businessId}/logo.${ext}`;
        const { error: uploadErr } = await withTimeout(
          supabase.storage.from('invoice-logos').upload(path, file, { upsert: true }),
          30_000,
          'Logo upload',
        );
        if (uploadErr) {
          log.error('[useInvoiceBranding] logo upload failed', uploadErr);
          return null;
        }
        const { data: urlData } = supabase.storage.from('invoice-logos').getPublicUrl(path);
        // Cache-bust so the freshly-overwritten file shows immediately.
        const logoUrl = `${urlData.publicUrl}?v=${Date.now()}`;
        await save({ logo_url: logoUrl }, userId);
        return logoUrl;
      } catch (e) {
        log.error('[useInvoiceBranding] logo upload threw', e);
        return null;
      }
    },
    [businessId, save],
  );

  return { settings, loading, error, save, uploadLogo, refresh };
}
